import { useEffect, useState, useMemo } from 'react'
import { collection, getDocs, query, orderBy, updateDoc, doc } from 'firebase/firestore'
import { db } from '../../services/firebase'
import { ArrowLeft, Loader, Users as UsersIcon, Mail, Calendar, Wallet, CheckCircle, XCircle, Search, ChevronLeft, ChevronRight, Eye, X, FileText, Phone, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'

export default function Usuarios() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [usersPerPage] = useState(10)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showUserModal, setShowUserModal] = useState(false)
  const [kycLoading, setKycLoading] = useState(false)

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)
      const usersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setUsers(usersData)
    } catch (error) {
      console.error('Error fetching users:', error)
      toast.error('Error al cargar usuarios')
    } finally {
      setLoading(false)
    }
  }

  const openUserDetail = (user) => {
    setSelectedUser(user)
    setShowUserModal(true)
  }

  const handleKycApprove = async () => {
    if (!selectedUser) return
    setKycLoading(true)
    try {
      await updateDoc(doc(db, 'users', selectedUser.id), {
        'kyc.verified': true,
        'kyc.verifiedAt': new Date(),
      })
      toast.success('KYC aprobado correctamente')
      await fetchUsers()
      setShowUserModal(false)
    } catch (error) {
      console.error('Error approving KYC:', error)
      toast.error('Error al aprobar KYC')
    } finally {
      setKycLoading(false)
    }
  }

  const handleKycReject = async () => {
    if (!selectedUser) return
    const reason = prompt('Motivo del rechazo:')
    if (!reason) return
    
    setKycLoading(true)
    try {
      await updateDoc(doc(db, 'users', selectedUser.id), {
        'kyc.verified': false,
        'kyc.documentSubmitted': false,
        'kyc.ineUrl': null,
        'kyc.rejectionReason': reason,
        'kyc.rejectedAt': new Date(),
      })
      toast.success('KYC rechazado. El usuario podrá subir un nuevo documento.')
      await fetchUsers()
      setShowUserModal(false)
    } catch (error) {
      console.error('Error rejecting KYC:', error)
      toast.error('Error al rechazar KYC')
    } finally {
      setKycLoading(false)
    }
  }

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      const searchLower = searchTerm.toLowerCase()
      return (
        user.email?.toLowerCase().includes(searchLower) ||
        user.username?.toLowerCase().includes(searchLower) ||
        user.fullName?.toLowerCase().includes(searchLower)
      )
    })
  }, [users, searchTerm])

  // Cálculos de paginación
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage)
  const indexOfLastUser = currentPage * usersPerPage
  const indexOfFirstUser = indexOfLastUser - usersPerPage
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser)

  // Precalcular estadísticas
  const stats = useMemo(() => {
    return {
      emailVerified: users.filter(u => u.emailVerified === true).length,
      phoneVerified: users.filter(u => u.phoneVerified === true).length,
      admins: users.filter(u => u.role === 'admin').length,
      operators: users.filter(u => u.role === 'operator').length,
      totalBalance: users.reduce((sum, u) => sum + (u.wallet?.total || 0), 0)
    }
  }, [users])

  // Resetear a página 1 cuando se busca
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
    return date.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  return (
    <div className="min-h-screen bg-[#1A1A1A] text-[#F8F8F8] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/operator"
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F8F8F8] mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            Volver al Panel
          </Link>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#D4AF37] mb-2 flex items-center gap-3">
                <UsersIcon size={32} />
                Usuarios Registrados
              </h1>
              <p className="text-[#BDBDBD]">
                Lista completa de usuarios en la plataforma
              </p>
            </div>
            <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg px-4 py-2">
              <span className="text-[#BDBDBD]">Total:</span>
              <span className="ml-2 font-bold text-[#D4AF37] text-2xl">
                {users.length}
              </span>
            </div>
          </div>
        </div>

        {/* Search Bar */}
          {!searchTerm && filteredUsers.length > 0 && (
            <p className="mt-2 text-sm text-[#BDBDBD]">
              Mostrando {indexOfFirstUser + 1}-{Math.min(indexOfLastUser, filteredUsers.length)} de {filteredUsers.length} usuarios
            </p>
          )}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#BDBDBD]" size={20} />
            <input
              type="text"
              placeholder="Buscar por email, nombre o usuario..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#222222] border border-[#0F3D2E] rounded-lg pl-10 pr-4 py-3 text-[#F8F8F8] placeholder-[#BDBDBD] focus:outline-none focus:border-[#D4AF37]"
            />
          </div>
          {searchTerm && (
            <p className="mt-2 text-sm text-[#BDBDBD]">
              Mostrando {filteredUsers.length} de {users.length} usuarios
            </p>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="animate-spin text-[#D4AF37]" size={48} />
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="card bg-[#222222] border border-[#0F3D2E] p-12 text-center">
            <UsersIcon className="mx-auto mb-4 text-[#BDBDBD]" size={48} />
            <h3 className="text-xl font-semibold text-[#F8F8F8] mb-2">
              {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
            </h3>
            <p className="text-[#BDBDBD]">
              {searchTerm 
                ? 'Intenta con otro término de búsqueda'
                : 'Los usuarios registrados aparecerán aquí.'
              }
            </p>
          </div>
        ) : (
          <div className="card bg-[#222222] border border-[#0F3D2E] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-[#F8F8F8]">
                <thead>
                  <tr className="bg-[#0F3D2E]">
                    <th className="px-4 py-3 text-left font-semibold">#</th>
                    <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Rol</th>
                    <th className="px-4 py-3 text-left font-semibold">Wallet</th>
                    <th className="px-4 py-3 text-left font-semibold">Verificado</th>
                    <th className="px-4 py-3 text-left font-semibold">Registro</th>
                    <th className="px-4 py-3 text-left font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#0F3D2E]">
                  {currentUsers.map((user, index) => (
                    <tr key={user.id} className="hover:bg-[#1A1A1A] transition-colors">
                      <td className="px-4 py-4 text-[#BDBDBD]">
                        {indexOfFirstUser + index + 1}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#0F3D2E] rounded-full flex items-center justify-center">
                            <UsersIcon size={20} className="text-[#D4AF37]" />
                          </div>
                          <div>
                            <div className="font-medium text-[#F8F8F8]">
                              {user.username || user.fullName || 'Sin nombre'}
                            </div>
                            {user.fullName && user.username && (
                              <div className="text-xs text-[#BDBDBD]">
                                @{user.username}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Mail size={14} className="text-[#BDBDBD]" />
                          <span className="text-[#F8F8F8]">{user.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                          user.role === 'admin' 
                            ? 'bg-[#B11217]/20 text-[#EF4444]' 
                            : user.role === 'operator'
                            ? 'bg-[#D4AF37]/20 text-[#D4AF37]'
                            : 'bg-[#0F3D2E]/50 text-[#F8F8F8]'
                        }`}>
                          {user.role === 'admin' ? 'Admin' : user.role === 'operator' ? 'Operador' : 'Usuario'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <Wallet size={14} className="text-[#D4AF37]" />
                          <span className="font-bold text-[#D4AF37]">
                            ${user.wallet?.total?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        {user.wallet && (
                          <div className="text-xs text-[#BDBDBD] mt-1">
                            Dep: ${user.wallet.deposited?.toFixed(0) || '0'} | 
                            Gan: ${user.wallet.winnings?.toFixed(0) || '0'}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          {/* Email Badge */}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            user.emailVerified === true 
                              ? 'bg-[#22C55E]/20 text-[#22C55E]' 
                              : 'bg-[#BDBDBD]/20 text-[#BDBDBD]'
                          }`}>
                            {user.emailVerified === true ? <CheckCircle size={10} /> : <XCircle size={10} />}
                            Email
                          </span>
                          {/* Phone Badge */}
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            user.phoneVerified === true 
                              ? 'bg-[#22C55E]/20 text-[#22C55E]' 
                              : 'bg-[#BDBDBD]/20 text-[#BDBDBD]'
                          }`}>
                            {user.phoneVerified === true ? <CheckCircle size={10} /> : <XCircle size={10} />}
                            Tel
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 text-[#BDBDBD]">
                          <Calendar size={14} />
                          <span>{formatDate(user.createdAt)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => openUserDetail(user)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0F3D2E] hover:bg-[#D4AF37] text-[#F8F8F8] rounded-lg transition-colors border border-[#0F3D2E] hover:border-[#D4AF37]"
                        >
                          <Eye size={14} />
                          Ver
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="bg-[#1A1A1A] border-t border-[#0F3D2E] px-4 py-3 flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn-sm bg-[#222222] text-[#F8F8F8] hover:bg-[#2A2A2A] border border-[#0F3D2E] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn-sm bg-[#222222] text-[#F8F8F8] hover:bg-[#2A2A2A] border border-[#0F3D2E] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-[#BDBDBD]">
                      Mostrando <span className="font-medium text-[#F8F8F8]">{indexOfFirstUser + 1}</span> a{' '}
                      <span className="font-medium text-[#F8F8F8]">{Math.min(indexOfLastUser, filteredUsers.length)}</span> de{' '}
                      <span className="font-medium text-[#F8F8F8]">{filteredUsers.length}</span> usuarios
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-[#0F3D2E] bg-[#222222] text-sm font-medium text-[#F8F8F8] hover:bg-[#2A2A2A] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      
                      {/* Números de página */}
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => {
                        // Mostrar solo algunas páginas alrededor de la actual
                        if (
                          number === 1 ||
                          number === totalPages ||
                          (number >= currentPage - 1 && number <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={number}
                              onClick={() => paginate(number)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === number
                                  ? 'z-10 bg-[#D4AF37] border-[#D4AF37] text-[#1A1A1A]'
                                  : 'bg-[#222222] border-[#0F3D2E] text-[#F8F8F8] hover:bg-[#2A2A2A]'
                              }`}
                            >
                              {number}
                            </button>
                          )
                        } else if (
                          number === currentPage - 2 ||
                          number === currentPage + 2
                        ) {
                          return <span key={number} className="relative inline-flex items-center px-4 py-2 border border-[#0F3D2E] bg-[#222222] text-sm font-medium text-[#BDBDBD]">...</span>
                        }
                        return null
                      })}
                      
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-[#0F3D2E] bg-[#222222] text-sm font-medium text-[#F8F8F8] hover:bg-[#2A2A2A] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stats Summary */}
        {!loading && users.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
              <p className="text-sm text-[#BDBDBD]">Email Verificado</p>
              <p className="text-2xl font-bold text-[#22C55E]">
                {stats.emailVerified}
              </p>
            </div>
            <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
              <p className="text-sm text-[#BDBDBD]">Teléfono Verificado</p>
              <p className="text-2xl font-bold text-[#22C55E]">
                {stats.phoneVerified}
              </p>
            </div>
            <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
              <p className="text-sm text-[#BDBDBD]">Administradores</p>
              <p className="text-2xl font-bold text-[#EF4444]">
                {stats.admins}
              </p>
            </div>
            <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
              <p className="text-sm text-[#BDBDBD]">Operadores</p>
              <p className="text-2xl font-bold text-[#D4AF37]">
                {stats.operators}
              </p>
            </div>
            <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg p-4">
              <p className="text-sm text-[#BDBDBD]">Saldo Total</p>
              <p className="text-2xl font-bold text-[#D4AF37]">
                ${stats.totalBalance.toFixed(2)}
              </p>
            </div>
          </div>
        )}

        {/* User Detail Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#222222] border border-[#0F3D2E] rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex justify-between items-center p-6 border-b border-[#0F3D2E]">
                <h2 className="text-2xl font-bold text-[#D4AF37]">Detalle del Usuario</h2>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="text-[#BDBDBD] hover:text-[#F8F8F8] transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* User Info */}
                <div className="bg-[#1A1A1A] border border-[#0F3D2E] rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-[#D4AF37] mb-4">Información Personal</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-[#BDBDBD]">Nombre Completo</p>
                      <p className="text-[#F8F8F8] font-medium">{selectedUser.fullName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#BDBDBD]">Usuario</p>
                      <p className="text-[#F8F8F8] font-medium">{selectedUser.username || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#BDBDBD]">Email</p>
                      <p className="text-[#F8F8F8] font-medium flex items-center gap-2">
                        <Mail size={14} />
                        {selectedUser.email}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[#BDBDBD]">Teléfono</p>
                      <p className="text-[#F8F8F8] font-medium flex items-center gap-2">
                        <Phone size={14} />
                        {selectedUser.phone || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[#BDBDBD]">Fecha de Nacimiento</p>
                      <p className="text-[#F8F8F8] font-medium">{selectedUser.dateOfBirth || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-[#BDBDBD]">Rol</p>
                      <p className="text-[#F8F8F8] font-medium capitalize">{selectedUser.role}</p>
                    </div>
                  </div>
                </div>

                {/* Verification Status */}
                <div className="bg-[#1A1A1A] border border-[#0F3D2E] rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-[#D4AF37] mb-4">Estado de Verificación</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      {selectedUser.emailVerified ? (
                        <CheckCircle className="text-[#22C55E]" size={20} />
                      ) : (
                        <XCircle className="text-[#BDBDBD]" size={20} />
                      )}
                      <span className="text-[#F8F8F8]">Email Verificado</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedUser.phoneVerified ? (
                        <CheckCircle className="text-[#22C55E]" size={20} />
                      ) : (
                        <XCircle className="text-[#BDBDBD]" size={20} />
                      )}
                      <span className="text-[#F8F8F8]">Teléfono Verificado</span>
                    </div>
                  </div>
                </div>

                {/* Wallet Info */}
                <div className="bg-[#1A1A1A] border border-[#0F3D2E] rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-[#D4AF37] mb-4">Billetera</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-[#BDBDBD]">Total</p>
                      <p className="text-xl font-bold text-[#D4AF37]">
                        ${selectedUser.wallet?.total?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[#BDBDBD]">Depositado</p>
                      <p className="text-xl font-bold text-[#F8F8F8]">
                        ${selectedUser.wallet?.deposited?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-[#BDBDBD]">Ganado</p>
                      <p className="text-xl font-bold text-[#22C55E]">
                        ${selectedUser.wallet?.winnings?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* KYC Section */}
                <div className="bg-[#1A1A1A] border border-[#0F3D2E] rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-[#D4AF37] mb-4 flex items-center gap-2">
                    <FileText size={20} />
                    Verificación KYC
                  </h3>
                  
                  {selectedUser.kyc?.documentSubmitted ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2">
                        {selectedUser.kyc?.verified ? (
                          <>
                            <CheckCircle className="text-[#22C55E]" size={20} />
                            <span className="text-[#22C55E] font-medium">KYC Verificado</span>
                          </>
                        ) : (
                          <>
                            <Clock className="text-yellow-500" size={20} />
                            <span className="text-yellow-500 font-medium">KYC Pendiente de Revisión</span>
                          </>
                        )}
                      </div>

                      {/* KYC Document Image */}
                      {selectedUser.kyc?.ineUrl && (
                        <div>
                          <p className="text-sm text-[#BDBDBD] mb-2">Documento de Identidad:</p>
                          <a
                            href={selectedUser.kyc.ineUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block border border-[#0F3D2E] rounded-lg overflow-hidden hover:border-[#D4AF37] transition-colors"
                          >
                            <img
                              src={selectedUser.kyc.ineUrl}
                              alt="KYC Document"
                              className="w-full h-auto"
                            />
                          </a>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {!selectedUser.kyc?.verified && (
                        <div className="flex gap-3 mt-4">
                          <button
                            onClick={handleKycApprove}
                            disabled={kycLoading}
                            className="flex-1 bg-[#22C55E] hover:bg-[#16A34A] text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <CheckCircle size={18} />
                            {kycLoading ? 'Procesando...' : 'Aprobar KYC'}
                          </button>
                          <button
                            onClick={handleKycReject}
                            disabled={kycLoading}
                            className="flex-1 bg-[#B11217] hover:bg-[#8B0E13] text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <XCircle size={18} />
                            {kycLoading ? 'Procesando...' : 'Rechazar KYC'}
                          </button>
                        </div>
                      )}

                      {selectedUser.kyc?.rejectionReason && (
                        <div className="bg-[#B11217]/20 border border-[#B11217] rounded-lg p-3">
                          <p className="text-sm text-[#EF4444]">
                            <strong>Motivo de rechazo:</strong> {selectedUser.kyc.rejectionReason}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <FileText className="mx-auto mb-2 text-[#BDBDBD]" size={32} />
                      <p className="text-[#BDBDBD]">No ha enviado documentos KYC</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-[#0F3D2E] flex justify-end">
                <button
                  onClick={() => setShowUserModal(false)}
                  className="btn bg-[#0F3D2E] text-[#F8F8F8] hover:bg-[#D4AF37] px-6 py-2 rounded-lg"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
